import { Stack, TextField } from '@mui/material'
import { RegisterOptions, UseFormReturn, useController } from 'react-hook-form'

interface InputFieldProps {
  required?: boolean
  name: string
  form?: UseFormReturn<any, any>
  style?: {
    height?: number
    borderRadius?: string
    width?: {
      xs?: string | number
    }
    marginTop?: string
  }
  padding?: string
  rules?: Exclude<
    RegisterOptions,
    'valueAsNumber' | 'valueAsDate' | 'setValueAs'
  >
  type?: string | undefined
  placeholder?: any
  label?: string
  autoFocus?: boolean
  inputRef?: any
  disabled?: boolean
  isRequired?: boolean
  value?: any
  onChange?: React.ChangeEventHandler<HTMLInputElement | HTMLTextAreaElement>
}

const InputFieldCalendar = ({
  isRequired,
  required,
  name,
  form,
  placeholder,
  label,
  padding,
  type,
  style,
  inputRef,
  disabled,
  value,
  onChange,
  ...props
}: InputFieldProps) => {
  const { control } = form || { control: null }

  const {
    field,
    formState: { errors },
  } = control
    ? // eslint-disable-next-line react-hooks/rules-of-hooks
      useController({ name, control })
    : {
        field: { onChange, value, onBlur: undefined, ref: undefined },
        formState: { errors: undefined },
      }

  return (
    <Stack>
      <TextField
        required={required}
        fullWidth
        id={name}
        label={label}
        placeholder={placeholder}
        inputRef={inputRef}
        variant="filled"
        type={type}
        value={field.value ?? ''}
        inputProps={{ ...field, value: field.value ?? '' }}
        InputProps={{ disableUnderline: true }}
        onChange={field.onChange}
        sx={{
          gap: '4px',
          lineHeight: '115%',
          color: !!errors?.[name] ? 'text.main' : '#FF3030',
          borderColor: !!errors?.[name] ? '#FF3030' : '#F1F1F1',
          backgroundColor: !!errors?.[name]
            ? 'error.main'
            : 'background.overrideDefault',
          '&:hover': {
            borderColor: !!errors?.[name] ? '#FF3030' : '#004F4B',
            backgroundColor: !!errors?.[name]
              ? 'error.main'
              : 'background.overrideDefault',
          },
          [`& .MuiInputLabel-root`]: {
            color: !!errors?.[name] ? 'primary.contrastText' : 'text.main',
            lineHeight: '16px',
            fontWeight: '700',
            fontSize: '12px',
            left: '4px',
          },
          [`& .Mui-disabled`]: {
            color: 'text.main',
            backgroundColor: !!errors?.[name]
              ? 'error.main'
              : 'background.overrideDefault',
            borderRadius: 'inherit',
          },
          ...(isRequired && {
            [`& .MuiInputLabel-root::after`]: {
              content: '"*"',
              color: '#ED2121',
              fontFamily: 'inherit',
              verticalAlign: 'middle',
              fontSize: '14px',
            },
          }),
          [`& .MuiInputLabel-shrink.Mui-focused`]: {
            color: !!errors?.[name] ? 'primary.contrastText' : 'text.main',
          },

          [`& .MuiInputBase-root`]: {
            color: 'text.main',
            borderBottomLeftRadius: style?.borderRadius,
            borderBottomRightRadius: style?.borderRadius,
            borderTopRightRadius: style?.borderRadius,
            borderTopLeftRadius: style?.borderRadius,
            backgroundColor: !!errors?.[name]
              ? 'error.main'
              : 'background.overrideDefault',
          },
          [`& .MuiInputBase-input`]: {
            borderBottomLeftRadius: style?.borderRadius,
            borderBottomRightRadius: style?.borderRadius,
          },

          [`& .MuiFilledInput-input`]: {
            maxHeight: '17px',
            paddingLeft: '16px',
          },
          borderRadius: '8px',
          width: style?.width?.xs,
          ...style,
        }}
        {...props}
      />
    </Stack>
  )
}

export default InputFieldCalendar
